import { HOVER_BACKGROUND } from "@components/common/faro-card/faro-card";
import { ProjectListItemCardMobile } from "@components/project/project-list-item-card-mobile";
import { WorldMapHandle } from "@components/world-map/world-map-types";
import { BaseProjectProps } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { Box } from "@mui/material";
import { useProjectMenuOptions } from "@pages/projects/use-project-menu-options";
import { CommonStyles } from "@styles/common-styles";

interface Props extends BaseProjectProps {
  /** Callback to navigate to a project position on the projects map */
  goTo?: WorldMapHandle["goTo"];

  /** Called when the card hover state changes. Receives the project id on enter, null on leave. */
  onHoverChange?: (projectId: string | null) => void;

  /** Whether the card should show its highlighted (hovered) style */
  isHighlighted?: boolean;

  /** The active sort criteria */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;
}

/**
 * @returns A card with information of the passed project.
 */
export function ProjectsMapListItem({
  project,
  goTo,
  onHoverChange,
  isHighlighted,
  sortBy,
}: Props): JSX.Element {
  const menuItems = useProjectMenuOptions({
    contextType: "project-map-view",
    goTo,
  });

  // A card is clickable if the project has a position and the goTo function is available.
  const isCardClickable = !!project.position && !!goTo;

  /** Centers the map on the project's position */
  function onCardClick(): void {
    if (project.position && goTo) {
      goTo(project.position);
    }
  }

  return (
    <Box
      onMouseEnter={() => onHoverChange?.(project.id)}
      onMouseLeave={() => onHoverChange?.(null)}
      sx={{
        border: CommonStyles.Borders.gray200Divider,
        borderRadius: "8px",
        marginY: "1.5px",
        padding: "8px",
        backgroundColor: isHighlighted ? HOVER_BACKGROUND : undefined,
        "&:hover": {
          backgroundColor: isCardClickable ? HOVER_BACKGROUND : undefined,
        },
      }}
    >
      <ProjectListItemCardMobile
        project={project}
        sortBy={sortBy}
        contextType="project-map-view"
        onCardClick={isCardClickable ? onCardClick : undefined}
        menuItems={menuItems}
      />
    </Box>
  );
}
